'use client';

/**
 * DetailsCard — first sub-card on the Insights surface.
 *
 * Selection-aware via `useViewportSelection`: same `rfi_details`
 * strategy + entity-schema rendering today's Copilot Details tab
 * uses. The rail card is presentation chrome only — content adapts
 * per entity type (RFQ, vendor, product, etc.) exactly as the
 * existing Details tab does, so no per-type branching here.
 *
 * Chrome (label + icon) comes from `useCardIdentity()` — identity only
 * (label / icon / content_key), sourced from INSIGHTS_CARDS server-side /
 * CardPlacement on canvas mounts. No hardcoded "Details" string, no
 * `lucide-react` icon import. This card paints from tokens, not a
 * typography bundle, so it must NOT call `useCardChrome()` (which resolves
 * a bundle and alarms when a tokens card has none).
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { DetailsPanel } from '../details/details-panel';
import { RailCard } from './rail-card';

// Direct strategy name. The existing `rfi-details-panel.tsx` does the
// same — `('rfi', 'details')` capability lookup returns null because
// the strategy registers with no explicit `action` field, so the
// fallback emits `action="read"`.
const RFI_DETAILS_STRATEGY = 'rfi_details';

export function DetailsCard({
  entityId: entityIdProp,
  entityType: entityTypeProp,
}: { entityId?: string; entityType?: string } = {}) {
  // Relocatable: when the mount hands (type, sid) — the Ask overlay pins the
  // dispatched entity via renderer-slot-card — bind to THAT; else fall back to
  // the ambient viewport selection (mode1 Insights). rfi_details keys its
  // fetch on both, so both are props.
  const { entityType: vsType, entityId: vsId } = useViewportSelection();
  const entityType = entityTypeProp ?? vsType;
  const entityId = entityIdProp ?? vsId;
  const { label, Icon, contentKey } = useCardIdentity();

  const { data, isLoading, error } = useStrategyData<{ data?: Record<string, unknown> }>(
    RFI_DETAILS_STRATEGY,
    { entity_type: entityType ?? '', entity_sid: entityId ?? '' },
    !!entityType && !!entityId,
  );

  const title = label ?? '';

  // Scene export — register the Details rail card into the capture envelope.
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'details',
    status: (data ? 'loaded' : entityType && entityId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: entityType && entityId
      ? ['strategy-data', RFI_DETAILS_STRATEGY, { entity_type: entityType, entity_sid: entityId }]
      : null,
    text: title || 'Details',
    json: { entity_type: entityType ?? null, entity_id: entityId ?? null },
  }), [contentKey, data, entityType, entityId, title]));

  if (!entityType || !entityId) {
    return (
      <RailCard title={title} icon={Icon}>
        <div className="text-xs text-muted-foreground">Select an item to see details</div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title={title} icon={Icon}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading details…
        </div>
      </RailCard>
    );
  }

  if (error || !data) {
    return (
      <RailCard title={title} icon={Icon}>
        <div className="text-xs text-destructive">
          {error ? `Failed to load: ${(error as Error).message}` : 'No data'}
        </div>
      </RailCard>
    );
  }

  const bffData = (data.data ?? {}) as Record<string, unknown>;

  return (
    <RailCard title={title} icon={Icon}>
      <DetailsPanel entityType={entityType} entityId={entityId} data={bffData} />
    </RailCard>
  );
}
